import { clearDemoScheduleLocal } from "../utils/clearDemoScheduleLocal";
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from "./AuthContext";
import { useTranscription } from "./TranscriptionContext";
import logger from '../utils/logger';
import {
  DEMO_TRANSCRIPT,
  DEMO_TIMING,
  MODAL_TYPES,
  MODAL_TEMPLATES,
  normalizeTimeString,
  getDemoStepForActionType
} from '../demo/annualReviewDemoLogic';
import { flushSync } from 'react-dom';
import MeetingDebriefingRoom from '../components/domains/advisor-portal/MeetingDebriefingRoom';

// Safety switch for legacy restore logic
const SKIP_LEGACY_RESTORE = localStorage.getItem("skipLegacyRestore") === "true";

// Helper function to clear all demo-related storage
export const clearDemoStorage = () => {
  localStorage.removeItem("demoSelectedJourney");
  localStorage.removeItem("isDemoMode");
  localStorage.removeItem("demoStep");
  localStorage.removeItem("demoSeenSteps");
  localStorage.removeItem("resumingDemo");
  localStorage.removeItem(DEMO_STORAGE_KEY);
  localStorage.removeItem("currentIndex");
  localStorage.removeItem("transcript");
  localStorage.removeItem("isPlaying");
  localStorage.removeItem("isPaused");
  // Do NOT clear completed demos here - they should persist
};

// Helper to get/set completed demos
const COMPLETED_DEMOS_KEY = 'completed_demos';
const getCompletedDemos = () => {
  try {
    const stored = localStorage.getItem(COMPLETED_DEMOS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Failed to parse completed demos:', error);
    return {};
  }
};

export const setCompletedDemo = (demoId) => {
  try {
    const completed = getCompletedDemos();
    completed[demoId] = new Date().toISOString();
    localStorage.setItem(COMPLETED_DEMOS_KEY, JSON.stringify(completed));
    return completed;
  } catch (error) {
    console.error('Failed to save completed demo:', error);
    return {};
  }
};

const clearCompletedDemo = (demoId) => {
  try {
    const completed = getCompletedDemos();
    delete completed[demoId];
    localStorage.setItem(COMPLETED_DEMOS_KEY, JSON.stringify(completed));
    return completed;
  } catch (error) {
    console.error('Failed to clear completed demo:', error);
    return {};
  }
};

// Helper to handle return to annual review
const handleReturnToAnnualReview = (transcriptionDispatch, setIsPaused, setCurrentIndex, setTranscript, setState) => {
  console.log('🚀 Forcing return to annual review with complete cleanup');
  clearDemoStorage();
  transcriptionDispatch({ type: 'STOP_RECORDING' });
  transcriptionDispatch({ type: 'CLEAR_TRANSCRIPT' });
  setIsPaused(true);
  setCurrentIndex(0);
  setTranscript([]);
  setState({
    isDemoMode: false,
    isPlaying: false,
    isPaused: true,
    isRecording: false,
    activeModal: null,
    activeSpotlight: null,
    selectedJourney: null,
    demoStep: 0,
    currentIndex: 0,
    showBriefingRoom: false,
    hasStartDemo: false,
    hasOnAction: false,
    transcript: []
  });
  window.location.href = '/advisor-portal/client-database/client-database/household-profile/253/ar-profile/2025/1';
};

const DEMO_STORAGE_KEY = 'demo_state';

// Helper function to clear all state
const getInitialState = (preserveCompletedDemos = true, keepDemoMode = false) => {
  return {
    selectedJourney: null,
    isDemoMode: keepDemoMode,
    demoStep: 0,
    activeModal: null,
    isPaused: false,
    isPlaying: false,
    transcript: [],
    currentIndex: 0,
    lastAction: null,
    hasAcknowledgedDemo: false,
    completedDemos: preserveCompletedDemos ? getCompletedDemos() : [],
    isConfirmingStop: false
  };
};

const DemoContext = createContext();

export const useDemo = () => useContext(DemoContext);

export function DemoProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { 
    transcript,
    startRecording,
    stopRecording,
    isRecording,
    isPaused,
    setTranscript,
    setCurrentIndex,
    setIsPaused,
    setIsRecording
  } = useTranscription();
  

  
  // Keep a ref to the latest transcript for callbacks
  const transcriptRef = useRef(transcript);
  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);
  const shuttingDownRef = useRef(false);
  
  // Build a map actionType → its index in DEMO_TRANSCRIPT (runs once)
  const transcriptIndexMap = useMemo(() => {
    return DEMO_TRANSCRIPT.reduce((map, entry, idx) => {
      if (entry.action?.type) map[entry.action.type] = idx;
      return map;
    }, {});
  }, []);
  
  const [state, setState] = useState(() => ({
    ...getInitialState(),
  }));

  // NOTE: Do not auto-acknowledge demo mode. Acknowledgement should occur via the
  // explicit "Let's Begin"/intro modal flow so ordering is deterministic.

  // Update the resetDemo function to properly clean up state
  const handleResetDemo = useCallback((demoId) => {
    console.log('🔄 Resetting demo:', demoId);
    
    // Clear the completed status for this demo
  clearDemoScheduleLocal();
    if (demoId) {
      clearCompletedDemo(demoId);
    }
    
    // Clear all demo storage
    clearDemoStorage();
    
    // Reset all state to initial values (demo mode OFF so click trap is removed)
    const baseState = getInitialState(true, false); // preserve completed demos, leave demo mode off
    setState({
      ...baseState,
      activeModal: null,
      transcript: [],
      currentIndex: 0,
      demoStep: 0,
      selectedJourney: null,
      isPlaying: false,
      isPaused: true,
    });
    
    // Reset transcription context
    stopRecording();
    setTranscript([]);
    setCurrentIndex(0);
    setIsPaused(true);
    setIsRecording(false);
    
    // Navigate back to home
    navigate('/home-dashboard');
  }, [navigate, setTranscript, setCurrentIndex, setIsPaused, setIsRecording, stopRecording]);

  // Persist state changes to storage
  useEffect(() => {
    try {
      if (state.isDemoMode) {
        localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(state));
      } else {
        localStorage.removeItem(DEMO_STORAGE_KEY);
      }
    } catch (error) {
      logger.error('🎮 Failed to persist demo state:', error);
    }
  }, [state]);

  // Define the return to annual review handler at component level
  const handleReturnToAnnualReview = useCallback(() => {
    console.log('🔄 Return to annual review clicked');
    
    // Pause recording
    setIsPaused(true);
    
    // Navigate with state to preserve transcript and demo state
    navigate('/advisor-portal/client-database/household-profile/253/ar-profile/2025/1', {
      state: {
        preserveSidebar: true,
        resumeDemo: {
          type: 'RESUME_DEMO',
          payload: {
            transcript: transcriptRef.current,
            currentIndex: state.currentIndex,
            isExpanded: true // Force sidebar to stay expanded
          }
        }
      }
    });

    // Clear modal immediately to prevent flicker
    setState(prev => ({
      ...prev,
      activeModal: null
    }));
  }, [navigate, setIsPaused, state.currentIndex, setState]);

  const dispatchAction = useCallback((action) => {
    if (!action?.type) return;

    if (shuttingDownRef.current) {
      console.log('🛑 Demo shutting down; ignoring action:', action.type);
      return;
    }

    console.log('🎮 DemoContext handling action:', {
      type: action.type,
      currentState: state,
      isRecording
    });

    // If demo is already closed, ignore follow-on modals like SHOW_DEBRIEFING_ROOM
    if ((!state.isDemoMode || shuttingDownRef.current) && action.type === MODAL_TYPES.SHOW_DEBRIEFING_ROOM) {
      console.log('🙅‍♂️ Ignoring SHOW_DEBRIEFING_ROOM because demo is already closed');
      return;
    }

    // Special cases that need custom handling
    switch (action.type) {
      case 'STOP_DEMO': {
        console.log('🎯 [DemoContext] STOP_DEMO - stopping demo and returning to home');
        clearDemoStorage();
        clearDemoScheduleLocal();

        // Stop recording and clear transcript to avoid lingering state
        stopRecording();
        setTranscript([]);
        setCurrentIndex(0);
        setIsPaused(true);
        setIsRecording(false);

        // For stopping the demo completely, reset to initial state and disable demo mode
        setState({
          ...getInitialState(true, false),
          hasAcknowledgedDemo: false,
          selectedJourney: null,
          activeModal: null,
          currentIndex: 0,
          demoStep: 0,
          isPlaying: false,
          isPaused: true,
          isRecording: false,
        });
        // Request demo selection on next view (durable across navigation quirks)
        try { localStorage.setItem('demo_force_select', 'true'); } catch (_) {}
        navigate('/home-dashboard', { replace: true });
        return;
      }
      case MODAL_TYPES.SHARE_QUESTIONNAIRE: {
        console.log('[Demo] Opening ShareQuestionnaireModal', {
          isOpen: true,
          householdMembers: action.data?.householdMembers,
          householdId: action.data?.householdId,
          fromDemo: true
        });

        setState(prev => ({
          ...prev,
          isPaused: true,
          activeModal: {
            type: MODAL_TYPES.SHARE_QUESTIONNAIRE,
            props: {
              isOpen: true,
              fromDemo: true,
              householdId: action.data?.householdId || 253,
              householdMembers: action.data?.householdMembers || [
                { id: 1, name: 'Thomas Borer', email: 'thomas.borer@hotmail.com' },
                { id: 2, name: 'Mary Borer', email: 'mary.borer@yahoo.com' }
              ],
              onClose: () => {
                console.log('[Demo] ShareQuestionnaireModal closed');
                setState(p => ({
                  ...p,
                  activeModal: null,
                  isPaused: false
                }));
              }
            }
          }
        }));
        return;
      }

      case 'RESUME_ALLOCATIONS': {
        const { transcript: newTranscript } = action.payload;
        console.log('📝 RESUME_ALLOCATIONS action.payload:', {
          transcriptLength: newTranscript?.length,
          lastLine: newTranscript?.[newTranscript.length - 1]
        });

        // Find OPEN_ALLOCATIONS in incoming transcript
        const allocIndex = newTranscript.findIndex(
          line => line.time === "07:35" && line.action?.type === "OPEN_ALLOCATIONS"
        );
        console.log('📝 Found OPEN_ALLOCATIONS at index:', allocIndex);

        if (allocIndex >= 0) {
          // Reconstruct full transcript by appending remaining lines from DEMO_TRANSCRIPT
          const fullTranscript = [
            ...newTranscript,
            ...DEMO_TRANSCRIPT.slice(allocIndex + 1)
          ];
          console.log('📝 Built full transcript for resume:', {
            originalLength: newTranscript.length,
            fullLength: fullTranscript.length,
            preview: fullTranscript.slice(allocIndex, allocIndex + 5)
          });

          // Set the full transcript and update state
          setTranscript(fullTranscript);
          setCurrentIndex(allocIndex);
          setIsPaused(true);

          setState(prev => ({
            ...prev,
            demoStep: 5,
            isPaused: true,
            currentIndex: allocIndex,
            isPlaying: true,
            activeModal: {
              type: MODAL_TYPES.ALLOC_OVERVIEW,
              props: {
                ...MODAL_TEMPLATES[MODAL_TYPES.ALLOC_OVERVIEW],
                isOpen: true,
                onClose: () => {
                  console.log('🔄 ALLOC_OVERVIEW modal closing, resuming playback with full transcript:', {
                    currentIndex: allocIndex + 1,
                    transcriptLength: fullTranscript.length
                  });
                  setState(p => ({
                    ...p,
                    activeModal: null,
                    isPaused: false,
                    isPlaying: true,
                    currentIndex: allocIndex + 1,
                    actionLockIndex: null
                  }));
                  setIsPaused(false);
                  setCurrentIndex(allocIndex + 1);
                }
              }
            }
          }));
        } else {
          console.error('❌ Could not find OPEN_ALLOCATIONS in transcript');
        }
        return;
      }

      case MODAL_TYPES.SMART_FOCUS:
        const { spotlightId, message, demoStep, toolIntroduction } = action.data;
        const template = MODAL_TEMPLATES[MODAL_TYPES.SMART_FOCUS];

        const triggerSpotlight = () => {
          console.log('[DemoContext] triggerSpotlight called with:', { spotlightId, message });
          
          const tryShowSpotlight = () => {
            const el = document.querySelector(spotlightId);
            console.log('[DemoContext] Found spotlight target:', el);
            if (el) {
              console.log('[DemoContext] Dispatching spotlight event');
              window.dispatchEvent(new CustomEvent("spotlight", {
                detail: { 
                  target: el, 
                  label: message,
                  showCloseButton: true,
                  disableAutoAdvance: true,
                                    onClose: () => {
                    console.log('[DemoContext] Spotlight onClose called');
                    setState(p => ({ 
                      ...p, 
                      activeSpotlight: null,
                      isPaused: false
                    }));
                    // Only advance demo after user clicks X on overlay
                    dispatchAction({ type: 'ADVANCE_DEMO' });
                  }
                }
              }));
            } else {
              console.log('[DemoContext] Target not found, retrying in 100ms');
              setTimeout(tryShowSpotlight, 100);
            }
          };
          
          tryShowSpotlight();
        };

        setState(prev => ({
          ...prev,
          demoStep: demoStep ?? prev.demoStep,
          isPaused: true,
          isPlaying: false, // Keep playback off during Smart Focus
          activeModal: {
            type: MODAL_TYPES.SMART_FOCUS,
            props: {
              ...template,
              phased: toolIntroduction === true,
              isOpen: true,
              title: toolIntroduction
                ? '🧠 Tool Introduction: Smart Focus'
                : action.data.title,
              description: toolIntroduction
                ? template.description
                : action.data.description || message,
              dismissButtonLabel: toolIntroduction ? undefined : 'Dismiss',
              acknowledgementButtonLabel: toolIntroduction ? 'Got It' : undefined,
              buttonLabel: 'Focus',
              showDismissButton: !toolIntroduction,
              showConfirmButton: !toolIntroduction,
              showConfirmOnly: toolIntroduction,
              showViewResearchButton: false,
              contextDetectionSummary: message,
              toolIntroduction: toolIntroduction,
              modalPhase: toolIntroduction ? 1 : 2,
              onAction: () => {
                setState(p => ({
                  ...p,
                  activeModal: null,
                  activeSpotlight: {
                    target: spotlightId,
                    label: message
                  }
                }));
                triggerSpotlight();
              },
              onDismiss: () => setState(p => ({ ...p, activeModal: null })),
              onAdvancePhase: toolIntroduction
                ? () => {
                    console.log("[DemoContext] onAdvancePhase called, current phase:", state.activeModal?.props.modalPhase, "-> 2");
                    setState(prev => ({
                      ...prev,
                      activeModal: {
                        ...prev.activeModal,
                        props: {
                          ...prev.activeModal.props,
                          modalPhase: 2,
                          toolIntroduction: false, // must be false now
                          title: template.secondaryTitle || action.data.title, // Use template's secondaryTitle if available
                                                    onAction: () => {
                            console.log('[DemoContext] SMART_FOCUS phase 2 onAction called');
                            setState(p => ({
                              ...p,
                              activeModal: null,
                              activeSpotlight: { target: spotlightId, label: message }
                            }));
                            triggerSpotlight();
                            // DO NOT advance transcript here - wait for spotlight close
                          }
                        }
                      }
                    }));
                    console.log("[DemoContext] SMART_FOCUS phase updated to 2");
                  }
                : undefined,
            
              onClose: () => setState(p => ({ ...p, activeModal: null }))
            }
          }
        }));
        return;

      case 'START_DEMO':
        if (state.isDemoMode && state.demoStep > 0) {
          console.log('🚫 Demo already started, ignoring START_DEMO action');
          return;
        }
        console.log('🎯 [DemoContext] START_DEMO action received');
        
        setState(prev => ({
          ...prev,
          isDemoMode: true,
          demoStep: 0,
          isPlaying: false,
          isPaused: true,
          isRecording: false,
          showBriefingRoom: true,
          activeModal: null,
          currentIndex: 0,
          spotlightId: null,
          activeSpotlight: null
        }));
        break;

      case 'CALL_CONFIRMED':
        console.log('🎯 [DemoContext] CALL_CONFIRMED action received');
        // Don't start recording yet - wait for user to click "Let's Begin"
        setState(prev => ({
          ...prev,
          demoStep: 2,
          isPlaying: false,
          isPaused: true,
          isRecording: false,
          currentIndex: 0,
          spotlightId: null,
          activeSpotlight: null,
          hasAcknowledgedDemo: true
        }));
        return;

      case MODAL_TYPES.AI_SIDEBAR_INTRODUCTION: {
        console.log('🎯 [DemoContext] Showing AI sidebar introduction');
        // Delay showing the modal to allow sidebar expansion to complete
        setTimeout(() => {
          setState(prev => ({
            ...prev,
            activeModal: {
              type: MODAL_TYPES.AI_SIDEBAR_INTRODUCTION,
              props: {
                isOpen: true,
                onClose: () => {
                  setState(p => ({ ...p, activeModal: null }));
                  dispatchAction({ type: 'START_RECORDING' });
                }
              }
            }
          }));
        }, 300); // Wait for sidebar transition (300ms)
        return;
      }

      case 'START_RECORDING':
        console.log('🎯 [DemoContext] START_RECORDING action received - THIS SHOULD START THE DEMO');
        console.log('🎯 [DemoContext] About to call startRecording() from transcription context');
        startRecording();
        console.log('🎯 [DemoContext] startRecording() called, now updating demo state');
        
        // Use flushSync to ensure state update happens synchronously
        flushSync(() => {
          setState(prev => {
            console.log('🎯 [DemoContext] Setting recording state:', {
              wasRecording: prev.isRecording,
              wasPlaying: prev.isPlaying,
              wasPaused: prev.isPaused
            });
            return {
              ...prev,
              isPlaying: true,
              isPaused: false,
              isRecording: true
            };
          });
        });
        
        // Ensure state is updated before playback effect runs
        console.log('🎯 [DemoContext] START_RECORDING state updated, should start playback');
        console.log('🎯 [DemoContext] Current state after update:', {
          isRecording: true,
          isPlaying: true,
          isPaused: false
        });
        return;

      case 'ADVANCE_DEMO': {
        setState(prev => {
          const nextIdx = prev.currentIndex + 1;
          // now update the transcript index with a concrete number
          setCurrentIndex(nextIdx);

          return {
            ...prev,
            demoStep: prev.demoStep + 1,
            // Only resume playback if recording is already active
            isPaused: prev.isRecording ? false : true,
            isPlaying: prev.isRecording ? true : false,
            isRecording: prev.isRecording,
            spotlightId: null,
            activeSpotlight: null,
            currentIndex: nextIdx
          };
        });
        return;
      }

      case 'RESUME_DEMO': {
        console.log('🎮 DemoContext handling RESUME_DEMO');
        
        // If already playing and not paused, ignore (extra safe)
        if (state.isPlaying && !state.isPaused) {
          console.log('⚠️ Demo already playing, ignoring RESUME_DEMO');
          return;
        }

        const { 
          transcript: resumeTranscript, 
          currentIndex: initialIndex,
          isRecording,
          isPaused
        } = action.payload || {};
        
        if (!resumeTranscript) {
          console.warn('⚠️ No transcript provided for resume');
          return;
        }

        // Find the next non-navigation line
        let finalIndex = initialIndex;
        while (
          finalIndex < resumeTranscript.length && 
          resumeTranscript[finalIndex]?.action?.type && 
          ['RETURN_REVIEW', 'NAV', 'ALLOC_HOLDING'].includes(resumeTranscript[finalIndex].action.type)
        ) {
          console.log('🎮 Skipping action line:', {
            index: finalIndex,
            type: resumeTranscript[finalIndex].action.type,
            text: resumeTranscript[finalIndex].text
          });
          finalIndex++;
        }
        
        console.log('🎮 Resume point found:', {
          startIndex: initialIndex,
          finalIndex,
          nextLine: resumeTranscript[finalIndex]
        });
        
        // Set transcript first
        setTranscript(resumeTranscript);
        
        // Then update index and state atomically
        setCurrentIndex(finalIndex);
        setIsPaused(false);
        setIsRecording(true);
        
        // Update demo state
        setState(prev => ({
          ...prev,
          isDemoMode: true,
          isPaused: false,
          isPlaying: true,
          isRecording: true,
          activeModal: null,
          activeSpotlight: null,
          spotlightId: null,
          currentIndex: finalIndex,
          demoStep: prev.demoStep || 0
        }));
        break;
      }

      case 'CLOSE_MODAL':
        console.log('🎯 Handling CLOSE_MODAL action');
        setState(prev => {
          // Special handling for end of demo
          const isEndOfDemo = prev.currentIndex >= DEMO_TRANSCRIPT.length - 1;
          return {
            ...prev,
            activeModal: null,
            isPaused: false,
            isPlaying: isEndOfDemo ? false : prev.isRecording,
            isDemoMode: isEndOfDemo ? false : prev.isDemoMode,
            currentIndex: prev.currentIndex + 1,
            spotlightId: null,
            activeSpotlight: null
          };
        });
        break;

      case 'END_DEMO': {
        console.log('🎯 [DemoContext] END_DEMO received');
        // Keep isDemoMode: true until debrief is complete
        setState(prev => ({
          ...prev,
          // Do not set isDemoMode: false yet - keep sidebar protected
          activeModal: {
            type: MODAL_TYPES.SHOW_DEBRIEFING_ROOM,
            props: {
              isOpen: true,
              component: MeetingDebriefingRoom,
              onClose: () => {
                console.log('🎯 [DemoContext] Debriefing room closed');
                // Only update modal state here
                setState(p => ({
                  ...p,
                  activeModal: null
                }));
              }
            }
          }
        }));
        return;
      }

      case 'RETURN_REVIEW': {
        console.log('🎯 [DemoContext] Showing modal for action:', {
          type: action.type,
          hasNavigation: true,
          targetPage: '/advisor-portal/client-database/household-profile/253/ar-profile/2025/1'
        });

        setState(prev => ({
          ...prev,
          isPaused: true,
          isPlaying: false,
          demoStep: getDemoStepForActionType(action.type) ?? prev.demoStep,
          activeModal: {
            type: action.type,
            props: {
              ...MODAL_TEMPLATES[action.type],
              isOpen: true,
              onReturnToAnnualReview: handleReturnToAnnualReview,
              onClose: handleReturnToAnnualReview,
              onDismiss: handleReturnToAnnualReview
            }
          }
        }));
        return;
      }

      case MODAL_TYPES.OPEN_ALLOCATIONS: {
        console.log('🎯 Handling OPEN_ALLOCATIONS action');
        const template = MODAL_TEMPLATES[action.type];
        console.log('🎯 Using template:', template);
        
        // First set the modal state
        setState(prev => {
          const modalState = {
            ...prev,
            demoStep: getDemoStepForActionType(action.type) ?? prev.demoStep,
            isPaused: true,
            activeModal: {
              type: action.type,
              props: {
                ...template,
                modalType: action.type, // Explicitly pass modalType
                isOpen: true,
                onClose: () => {
                  setState(p => ({
                    ...p,
                    activeModal: null,
                    isPaused: false,
                    isPlaying: true
                  }));
                  // Let the normal ADVANCE_DEMO flow handle the index increment
                }
              }
            }
          };
          console.log('🎯 Setting modal state:', modalState);
          return modalState;
        });

        // Then trigger the spotlight effect with retry logic
        const tryShowSpotlight = () => {
          console.log('🎯 Looking for spotlight target:', template.spotlightTarget);
          const target = document.querySelector(template.spotlightTarget);
          console.log('🎯 Found target:', target);
          
          if (target) {
            console.log('🎯 Dispatching spotlight event with:', {
              target,
              label: template.spotlightLabel
            });
            window.dispatchEvent(new CustomEvent("spotlight", {
              detail: {
                target,
                label: template.spotlightLabel
              }
            }));
          } else {
            console.log('🎯 Target not found, retrying in 100ms');
            setTimeout(tryShowSpotlight, 100);
          }
        };

        // Start the retry loop after a short delay to allow modal render
        setTimeout(tryShowSpotlight, 300);
        return;
      }

      case MODAL_TYPES.COMPLETE_DEMO: {
        console.log('🎯 [DemoContext] Showing COMPLETE_DEMO modal');
        const template = MODAL_TEMPLATES[action.type];
        setState(prev => ({
          ...prev,
          isPaused: true,
          isPlaying: false,
          currentIndex: DEMO_TRANSCRIPT.length,
          activeModal: {
            type: action.type,
            props: {
              ...template,
              isOpen: true,
              onAction: () => {
                console.log('🎯 [DemoContext] COMPLETE_DEMO onAction triggered — cleanly closing demo');
                shuttingDownRef.current = true;
                clearDemoStorage();
                window.dispatchEvent(new CustomEvent("spotlight", { detail: null }));
                setState(p => ({
                  ...p,
                  isDemoMode: false,
                  activeModal: null,
                  isPaused: false,
                  isPlaying: false,
                  currentIndex: DEMO_TRANSCRIPT.length
                }));
                // Navigate home after modal has time to fully close
                setTimeout(() => {
                  navigate('/home-dashboard');
                }, 300);
              },
              // Only use onClose if user dismisses without viewing debrief
              onClose: () => {
                setState(p => ({
                  ...p,
                  isDemoMode: false,
                  activeModal: null,
                  isPaused: false,
                  isPlaying: false,
                  currentIndex: DEMO_TRANSCRIPT.length
                }));
              }
            }
          }
        }));
        return;
      }

      case 'SHOW_DEBRIEFING_ROOM': {
        const { demoId } = action;
        console.log('🎯 [DemoContext] Showing debriefing room');
        setState(prev => ({
          ...prev,
          isPaused: true,
          isPlaying: false,
          activeModal: {
            type: 'SHOW_DEBRIEFING_ROOM',
            props: {
              isOpen: true,
              onClose: () => {
                console.log('🎯 [DemoContext] Debriefing room closed, navigating home');
                // When DebriefingRoom closes, update completion and navigate home
                if (demoId) setCompletedDemo(demoId);
                
                // Navigate first, then cleanup demo state after a short delay
                navigate('/home-dashboard');
                
                setTimeout(() => {
                  setState(p => ({
                    ...p,
                    isDemoMode: false,
                    activeModal: null,
                    isPaused: false,
                    isPlaying: false
                  }));
                }, 400);
              }
            }
          }
        }));
        return;
      }

      case 'SHOW_DEMO_ACKNOWLEDGMENT': {
        console.log('🎯 [DemoContext] Showing demo acknowledgment modal');
        setState(prev => ({
          ...prev,
          activeModal: {
            type: 'SHOW_DEMO_ACKNOWLEDGMENT',
            props: {
              isOpen: true,
              completed: true,
              onClose: () => setState(q => ({ ...q, activeModal: null })),
              onResetDemo: () => {
                setState(q => ({
                  ...q,
                  isDemoMode: true,
                  demoStep: 0,
                  currentIndex: 0,
                  transcript: [],
                  activeModal: null,
                  selectedJourney: 'Existing Client',
                }));
                navigate('/advisor-portal/annual-review-demo');
              }
            }
          }
        }));
        return;
      }

      case 'RESET_DEMO': {
        clearDemoScheduleLocal();
        console.log('🎯 [DemoContext] Resetting demo state');
        setState(prev => ({
          ...prev,
          isDemoMode: false,
          selectedJourney: null,
          activeModal: null,
          isPlaying: false,
          isPaused: true,
          transcript: [],
          currentIndex: 0,
          demoStep: 0,
          hasAcknowledgedDemo: false,
          completedDemos: getCompletedDemos()
        }));
        return;
      }

      case 'ALLOC_HOLDING': {
        console.log('🎯 [DemoContext] ALLOC_HOLDING – drilling into AAPL holding');
        setState(prev => ({
          ...prev,
          // advance the demo step
          demoStep: prev.demoStep + 1,
          // clear any spotlight/modal
          activeSpotlight: null,
          activeModal: null,
          // resume playback/advance transcript
          isPaused: false,
          isPlaying: true,
          currentIndex: prev.currentIndex + 1,
        }));
        return;
      }

      case 'ADD_REMINDER': {
        console.log("🎯 [DemoContext] ADD_REMINDER received");
        setState(prev => ({
          ...prev,
          lastAction: { id: Date.now(), type: "ADD_REMINDER" }
        }));
        return;
      }



      default: {
        // Handle any action that has a corresponding modal template
        const template = MODAL_TEMPLATES[action.type];
        if (template) {
          console.log('🎯 [DemoContext] Showing modal for action:', {
            type: action.type,
            hasNavigation: template.useInternalNavigation,
            targetPage: template.targetPage
          });
          const nextStep = getDemoStepForActionType(action.type);

          setState(prev => {
            const actionIdx = transcriptIndexMap[action.type];

            // Build modal props based on type
            const modalProps = {
              ...action.data,
              ...template,
              isOpen: true,
              // Use initialPhase if specified, otherwise start at phase 1 for phased modals
              modalPhase: template.phased ? (template.initialPhase || 1) : undefined,
              onClose: () => {
                console.log('🔄 Modal closed:', action.type);
                setState(p => ({
                  ...p,
                  activeModal: null,
                  isPaused: false,
                  isPlaying: true,
                  currentIndex: p.currentIndex + 1
                }));
                setIsPaused(false);
                setCurrentIndex(prev.currentIndex + 1);
              },
              onConfirm: () => {
                console.log('✅ Modal confirmed:', action.type);
                setState(p => ({
                  ...p,
                  activeModal: null,
                  isPaused: false,
                  isPlaying: true,
                  currentIndex: p.currentIndex + 1
                }));
                setIsPaused(false);
                setCurrentIndex(prev.currentIndex + 1);
              },
              // Add onAdvancePhase handler for phased modals
              onAdvancePhase: template.phased ? () => {
                console.log('🔄 Advancing phase for modal:', action.type);
                setState(p => {
                  const currentPhase = p.activeModal?.props?.modalPhase || 1;
                  const nextPhase = currentPhase + 1;
                  
                  // For 3-phase modals, we can go up to Phase 3
                  // For 2-phase modals, we stop at Phase 2
                  const maxPhase = template.researchSummary ? 3 : 2;
                  const finalPhase = Math.min(nextPhase, maxPhase);
                  
                  console.log(`🔄 Advancing from Phase ${currentPhase} to Phase ${finalPhase}`);
                  
                  return {
                    ...p,
                    activeModal: {
                      ...p.activeModal,
                      props: {
                        ...p.activeModal.props,
                        modalPhase: finalPhase
                      }
                    }
                  };
                });
              } : undefined
            };

            modalProps.onDismiss = modalProps.onClose;

            // Windows-resilient: make the Intro modal the gate that flips acknowledgement and starts recording
            if (action.type === 'TRANSCRIPTION_INTRODUCTION') {
              const proceed = () => {
                setState(p => ({
                  ...p,
                  hasAcknowledgedDemo: true,
                  activeModal: null,
                  isPaused: false,
                  isPlaying: true,
                  currentIndex: p.currentIndex + 1
                }));
                setIsPaused(false);
                setCurrentIndex(prev.currentIndex + 1);
                dispatchAction({ type: 'START_RECORDING' });
              };
              modalProps.onConfirm = proceed;
              modalProps.onClose = proceed;
              modalProps.onDismiss = proceed;
            }

            return {
              ...prev,
              isPaused: true,
              demoStep: nextStep != null ? nextStep : prev.demoStep,
              activeModal: {
                type: action.type,
                props: modalProps
              }
            };
          });
          return;
        }

        console.warn('Unknown action type:', action.type);
      }
    }
  }, [
    navigate, 
    startRecording, 
    isRecording, 
    state.isDemoMode, 
    state.demoStep,
    setTranscript,
    setCurrentIndex,
    setIsPaused,
    setIsRecording,
    stopRecording
  ]);

  // Updated stableDispatch implementation
  const dispatchRef = useRef();
  useEffect(() => {
    dispatchRef.current = dispatchAction;
  }, [dispatchAction]);
  
  const stableDispatch = useCallback((action) => {
    dispatchRef.current?.(action);
  }, []);

  // Transcript playback effect with minimal dependencies
  useEffect(() => {
    // If demo mode is off, do nothing
    if (!state.isDemoMode) return;

    console.log('🎯 [DemoContext] Playback effect running with state:', {
      isRecording: state.isRecording,
      isPlaying: state.isPlaying,
      isPaused: state.isPaused,
      hasModal: !!state.activeModal,
      currentIndex: state.currentIndex,
      hasAcknowledgedDemo: state.hasAcknowledgedDemo
    });
    
    // Don't start transcript until recording is active
    if (!state.isRecording) {
      return;
    }

    // Don't progress if not playing or paused or modal up
    if (!state.isPlaying || state.isPaused || state.activeModal) {
      console.log('⏸️ Playback paused:', {
        isRecording: state.isRecording,
        isPlaying: state.isPlaying,
        isPaused: state.isPaused,
        hasModal: !!state.activeModal,
        reason: !state.isPlaying ? 'not playing' : state.isPaused ? 'paused' : 'modal active'
      });
      return;
    }

    try {
      if (state.currentIndex >= DEMO_TRANSCRIPT.length) {
        console.log('✅ Reached end of transcript');
        dispatchAction({ type: MODAL_TYPES.END_DEMO });
        return;
      }

      const currentEntry = DEMO_TRANSCRIPT[state.currentIndex];
      if (!currentEntry) {
        console.error('❌ Invalid transcript entry at index:', state.currentIndex);
        return;
      }

      console.log('▶️ Playing transcript entry:', {
        index: state.currentIndex,
        entry: currentEntry,
        nextAction: currentEntry.action?.type
      });

      // Add current entry to transcript without incrementing index yet
      setState(prev => ({
        ...prev,
        transcript: [...(prev.transcript || []), currentEntry]
      }));

      // Handle action if exists
      if (currentEntry.action) {
        // Gate recording/introduction until user acknowledges
        if (!state.hasAcknowledgedDemo && ['START_RECORDING', 'TRANSCRIPTION_INTRODUCTION'].includes(currentEntry.action.type)) {
          console.log('⏸️ Blocking transcript action until acknowledged:', currentEntry.action.type);
          return;
        }
        requestAnimationFrame(() => {
          console.log('🎯 [DemoContext] Dispatching transcript action:', currentEntry.action);
          dispatchAction(currentEntry.action);
        });
        return;
      }

      const nextEntry = DEMO_TRANSCRIPT[state.currentIndex + 1];

      // Calculate delay until next line with safety bounds
      let delay = DEMO_TIMING.MIN_DELAY_MS;
      if (nextEntry) {
        try {
          const currentTime = normalizeTimeString(currentEntry.time);
          const nextTime = normalizeTimeString(nextEntry.time);
          delay = Math.min(
            DEMO_TIMING.MAX_DELAY_MS,
            Math.max(DEMO_TIMING.MIN_DELAY_MS, (nextTime - currentTime) * 1000)
          );
        } catch (error) {
          console.warn('⚠️ Error calculating delay, using minimum:', error);
          delay = DEMO_TIMING.MIN_DELAY_MS;
        }
      }

      console.log('⏱️ Scheduling next entry in:', delay, 'ms');

      // Only increment index when scheduling next entry
      const timeoutId = setTimeout(() => {
        setState(prev => {
          if (!prev.isPlaying || prev.isPaused || prev.activeModal) {
            console.log('🛑 Playback interrupted, not advancing');
            return prev;
          }
          return {
            ...prev,
            currentIndex: prev.currentIndex + 1
          };
        });
      }, delay);

      return () => {
        clearTimeout(timeoutId);
      };
    } catch (error) {
      console.error('❌ Error in playback effect:', error);
      setState(prev => ({ ...prev, isPaused: true }));
    }
  }, [state.isPlaying, state.isPaused, state.currentIndex, state.activeModal]);

  // Handle resume from navigation - single source of truth
  const location = useLocation();
  const resumeRef = useRef(false);

  useEffect(() => {
    const resume = location.state?.resumeDemo;
    if (resume && !resumeRef.current) {
      console.log('🎮 DemoContext handling navigation resume:', {
        resume,
        hasDispatchedResume: resumeRef.current
      });
      
      // Handle both single action and array of actions
      if (Array.isArray(resume)) {
        resume.forEach(action => dispatchAction(action));
      } else {
        dispatchAction(resume);
      }
      
      resumeRef.current = true;
      
      // Clean up resumeDemo from history
      const newState = { ...location.state };
      delete newState.resumeDemo;
      window.history.replaceState(newState, '', location.pathname);
    }
  }, [location.state, dispatchAction, location.pathname]);

  // Add cleanup effect
  useEffect(() => {
    console.log('🔄 Demo mode effect mounted/updated:', { isDemoMode: state.isDemoMode });
    
    // Only run cleanup when component is unmounting or demo mode is explicitly turned off
    return () => {
      if (!state.isDemoMode) {
        console.log('🧹 Cleanup effect triggered - demo mode turned off:', {
          isDemoMode: state.isDemoMode,
          demoStep: state.demoStep,
          isPlaying: state.isPlaying,
          isPaused: state.isPaused,
          currentIndex: state.currentIndex
        });
        
        setState(prev => ({
          ...prev,
          isPlaying: false,
          isPaused: true,
          // Do not reset transcript or recording state
          isRecording: prev.isRecording,
          // Clear any active modals or spotlights
          activeModal: null,
          activeSpotlight: null
        }));
      }
    };
  }, [state.isDemoMode]); // Only run cleanup when isDemoMode changes

  const startDemo = useCallback((journey) => {
    console.log('🎮 Starting demo:', journey);
    
    // Clear demo state before starting
    clearDemoStorage();
    
    setState(prev => ({
      ...prev,
      isDemoMode: true,
      selectedJourney: journey,
      demoStep: 0,
      isPaused: true,  // Keep paused initially
      isRecording: false,
      transcript: [],
      currentIndex: 0,
      isPlaying: false,  // Keep not playing initially
      activeModal: null,
      // Do not mark acknowledged yet; wait for acknowledgment modal
      hasAcknowledgedDemo: false
    }));

    // Add spotlight effect for the annual review meeting card
    if (journey === 'Existing Client') {
      setTimeout(() => {
        console.log('🎯 Setting up spotlight for annual review meeting card');
        const card = document.getElementById("annual-review-demo-meeting");
        if (card) {
          console.log('🎯 Found target card, dispatching spotlight event');
          window.dispatchEvent(
            new CustomEvent("spotlight", {
              detail: {
                target: card,
                label: "Click this meeting card to start the journey. You will be taken to the client's Annual Review Profile.",
                // In production we must navigate on click; don't rely on auto-advance only.
                disableAutoAdvance: true,
                onClose: () => {
                  // Clear spotlight immediately
                  window.dispatchEvent(new CustomEvent("spotlight", { detail: null }));
                  // Navigate to annual review profile and start demo flow there (briefing room)
                  navigate("/advisor-portal/client-database/household-profile/253/ar-profile/2025/1");
                  dispatchAction({ type: "START_DEMO" });
                },
                overlayStyles: {
                  pointerEvents: "none",
                  cursor: "default",
                  background: "rgba(0, 0, 0, 0.25)",
                  boxShadow: "0 0 0 2px #19fa6c, 0 0 20px #19fa6c40",
                  borderRadius: "8px"
                },
                spotlightStyles: {
                  pointerEvents: "none"
                },
                labelStyles: {
                  background: "linear-gradient(130deg, #212c23 60%, #17453c 100%)",
                  color: "#e3ffe9",
                  padding: "24px 30px",
                  borderRadius: "14px",
                  boxShadow: "0 4px 32px 0 #19fa6c38, 0 2px 12px rgba(0,0,0,0.25)",
                  fontSize: "1.07rem",
                  lineHeight: "1.5",
                  maxWidth: "410px",
                  minWidth: "320px",
                  position: "relative",
                  border: "none"
                },
                titleComponent: {
                  text: "Guided Demo:",
                  styles: {
                    color: "#19fa6c",
                    fontSize: "1.18rem",
                    fontWeight: "700",
                    marginBottom: "0.4em",
                    letterSpacing: "0.01em"
                  }
                },
                connectionLine: {
                  show: true,
                  styles: {
                    line: {
                      height: "3px",
                      background: "linear-gradient(90deg, #19fa6c 0%, #2eebd3 100%)",
                      boxShadow: "0 0 10px 1px #19fa6c80"
                    },
                    dot: {
                      size: "18px",
                      background: "#19fa6c",
                      boxShadow: "0 0 10px 4px #19fa6c88",
                      animate: true
                    }
                  }
                }
              }
            })
          );
        } else {
          console.warn('🎯 Target card not found, retrying in 500ms');
          // Retry once after a short delay in case the card hasn't rendered yet
          setTimeout(() => {
            const retryCard = document.getElementById("annual-review-demo-meeting");
            if (retryCard) {
              console.log('🎯 Found target card on retry, dispatching spotlight event');
              window.dispatchEvent(
                new CustomEvent("spotlight", {
                  detail: {
                    target: retryCard,
                    label: "Click this meeting card to start the journey. You will be taken to the client's Annual Review Profile.",
                    disableAutoAdvance: true,
                    onClose: () => {
                      window.dispatchEvent(new CustomEvent("spotlight", { detail: null }));
                      navigate("/advisor-portal/client-database/household-profile/253/ar-profile/2025/1");
                      dispatchAction({ type: "START_DEMO" });
                    },
                    overlayStyles: {
                      pointerEvents: "none",
                      cursor: "default",
                      background: "rgba(0, 0, 0, 0.25)",
                      boxShadow: "0 0 0 2px #19fa6c, 0 0 20px #19fa6c40",
                      borderRadius: "8px"
                    },
                    spotlightStyles: {
                      pointerEvents: "none"
                    },
                    labelStyles: {
                      background: "linear-gradient(130deg, #212c23 60%, #17453c 100%)",
                      color: "#e3ffe9",
                      padding: "24px 30px",
                      borderRadius: "14px",
                      boxShadow: "0 4px 32px 0 #19fa6c38, 0 2px 12px rgba(0,0,0,0.25)",
                      fontSize: "1.07rem",
                      lineHeight: "1.5",
                      maxWidth: "410px",
                      minWidth: "320px",
                      position: "relative",
                      border: "none"
                    },
                    titleComponent: {
                      text: "Guided Demo:",
                      styles: {
                        color: "#19fa6c",
                        fontSize: "1.18rem",
                        fontWeight: "700",
                        marginBottom: "0.4em",
                        letterSpacing: "0.01em"
                      }
                    },
                    connectionLine: {
                      show: true,
                      styles: {
                        line: {
                          height: "3px",
                          background: "linear-gradient(90deg, #19fa6c 0%, #2eebd3 100%)",
                          boxShadow: "0 0 10px 1px #19fa6c80"
                        },
                        dot: {
                          size: "18px",
                          background: "#19fa6c",
                          boxShadow: "0 0 10px 4px #19fa6c88",
                          animate: true
                        }
                      }
                    }
                  }
                })
              );
            } else {
              console.error('🎯 Target card not found after retry');
            }
          }, 500);
        }
      }, 100);
    }

    // Do not auto-navigate on selection; the calendar click starts the journey.
  }, [navigate, dispatchAction]);

  // Effect to handle user changes - preserve completed demos
  useEffect(() => {
    if (!user) {
      const completedDemos = getCompletedDemos(); // Keep completed demos
      localStorage.removeItem(DEMO_STORAGE_KEY);
      setState({
        selectedJourney: null,
        isDemoMode: false,
        demoStep: 0,
        activeModal: null,
        isPaused: false,
        isPlaying: false,
        transcript: [],
        currentIndex: 0,
        completedDemos // Preserve completed demos
      });
    } else if (user?.is_demo) {
      setState(prev => ({
        ...prev,
        isDemoMode: true,
        selectedJourney: null,
        completedDemos: getCompletedDemos() // Refresh completed demos
      }));
    }
  }, [user]);

  // Add onEffectComplete handler with better state management
  const onEffectComplete = useCallback(() => {
    console.log('🎯 Effect complete, handling cleanup and next action');

    // kill any DOM spotlight
    window.dispatchEvent(new CustomEvent("spotlight", { detail: null }));

    let nextAction;
    // synchronously clear our spotlight state
    flushSync(() => {
      setState(prev => {
        nextAction = prev.activeSpotlight?.nextAction;
        return {
          ...prev,
          activeSpotlight: null,
          spotlightId: null,
          isPaused: false,
          isPlaying: true,
          currentModalType: null
        };
      });
    });

    // and *then* immediately fire the next action
    if (nextAction) {
      console.log('🎯 Dispatching next action after spotlight cleanup:', nextAction);
      dispatchAction(nextAction);
    }
  }, [dispatchAction]);

  // Add modal action handler with better state management
  const onModalAction = useCallback((nextAction) => {
    console.log('🔄 Modal action:', nextAction);
    
    // Handle SET_MODAL_PHASE action
    if (nextAction?.type === 'SET_MODAL_PHASE') {
      setState(prev => ({
        ...prev,
        activeModal: {
          ...prev.activeModal,
          props: {
            ...prev.activeModal.props,
            modalPhase: nextAction.phase
          }
        }
      }));
      return;
    }
    
    setState(prev => {
      const nextState = {
        ...prev,
        activeModal: null,
        isPaused: false,
        isPlaying: true,
        currentIndex: prev.currentIndex + 1
      };

      // Schedule next action AFTER state update if needed
      if (nextAction) {
        Promise.resolve().then(() => {
          dispatchAction(nextAction);
        });
      }

      return nextState;
    });
  }, [dispatchAction]);

  const value = useMemo(() => ({
    ...state,
    onAction: dispatchAction,
    onEffectComplete,
    startDemo,
    pauseDemo: () => dispatchAction({ type: 'PAUSE_DEMO' }),
    resumeDemo: () => dispatchAction({ type: 'RESUME_DEMO' }),
    stopDemo: () => dispatchAction({ type: 'STOP_DEMO' }),
    resetDemo: handleResetDemo,
    setIsPaused: (paused) => setState(prev => ({ ...prev, isPaused: paused })),
    setIsRecording: (recording) => setState(prev => ({ ...prev, isRecording: recording })),
    setSelectedJourney: (journey) => setState(prev => ({ ...prev, selectedJourney: journey })),
    setIsConfirmingStop: (confirming) => setState(prev => ({ ...prev, isConfirmingStop: confirming })),
    setCurrentIndex: (indexOrFn) => {
      setState(prev => ({
        ...prev,
        currentIndex: typeof indexOrFn === 'function' ? indexOrFn(prev.currentIndex) : indexOrFn
      }));
      console.log('🎯 [DemoContext] Setting currentIndex:', typeof indexOrFn === 'function' ? 'via function' : indexOrFn);
    },
    onModalAction: (nextAction) => {
      console.log('🔄 Modal action:', nextAction);
      setState(prev => ({
        ...prev,
        activeModal: null,
        isPaused: false
      }));
      if (nextAction) {
        dispatchAction(nextAction);
      }
    }
  }), [
    state,
    dispatchAction,
    onEffectComplete,
    startDemo,
    handleResetDemo,
    navigate,
    setTranscript,
    setCurrentIndex,
    setIsPaused,
    setIsRecording
  ]);

  return (
    <DemoContext.Provider value={value}>
      {children}
    </DemoContext.Provider>
  );
}

// Debug component for visualizing demo state
export const DebugDemoState = () => {
  const {
    selectedJourney,
    currentSequence,
    pendingEffects,
    activeModal,
    activeSpotlight,
    isPaused
  } = useDemo();

  return (
    <pre
      style={{
        position: "fixed",
        bottom: 0,
        right: 0,
        background: "rgba(0,0,0,0.7)",
        color: "white",
        padding: 8,
        zIndex: 9999,
        maxHeight: "40%",
        overflow: "auto",
        fontSize: 12,
        fontFamily: "monospace",
        borderRadius: "4px 0 0 0",
        margin: 0,
        pointerEvents: "none"
      }}
    >
      Demo State:
      {JSON.stringify({
        selectedJourney,
        sequenceLength: currentSequence?.length,
        pendingEffects,
        activeModal,
        activeSpotlight,
        isPaused,
        skipLegacyRestore: localStorage.getItem("skipLegacyRestore") === "true"
      }, null, 2)}
    </pre>
  );
};

export default DemoProvider;
